<template>
  <router-link :to="detailRoute" class="market-tile">
    <div class="tile-main">
      <div class="logo">
        <img :src="token.image" :alt="token.name" loading="lazy" @error="onImgError" />
      </div>
      <div class="identity">
        <p class="name">{{ token.name }}</p>
        <p class="symbol">{{ token.symbol }}</p>
        <div class="badges" v-if="badges.length || token.sharia_compliant">
          <ShariaBadge :token="token" size="sm" />
          <TokenBadge v-for="b in badges" :key="b.key" :badge="b" />
        </div>
      </div>
      <div class="hero-side">
        <DealTypeBadge :badge="view.badge" />
        <div class="hero-metric">
          <template v-if="yieldHero">
            <span class="metric-value">{{ view.hero.value }}</span>
            <span class="metric-label">{{ view.hero.unit }}</span>
          </template>
          <template v-else>
            <div class="price-line">
              <span class="metric-value">{{ view.hero.value }}</span>
            </div>
            <div class="price-sub">
              <ChangePill :change="change" />
              <span class="metric-label">{{ view.hero.unit }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="tile-footer">
      <div class="stat-strip">
        <div class="stat" v-if="detail">
          <span class="stat-label">{{ detail.k }}</span>
          <span class="stat-value">{{ detail.v }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">Available to buy</span>
          <span class="stat-value">{{ available }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">Min. buy</span>
          <span class="stat-value">{{ minBuy }}</span>
        </div>
      </div>
      <span class="buy-link">
        {{ ctaLabel }}
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </div>
  </router-link>
</template>

<script>
import TokenBadge from "./TokenBadge.vue";
import ChangePill from "./ChangePill.vue";
import * as ex from "@/utils/exchange.js";
import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
import ShariaBadge from "@/components/general/ShariaBadge.vue";
import { DealKinds, listingView } from "@/utils/trade-view";

export default {
  name: "MarketTile",
  components: { TokenBadge, ChangePill, DealTypeBadge, ShariaBadge },
  props: {
    token: { type: Object, required: true },
  },
  computed: {
    // Badge, headline and pills by kind of deal (utils/trade-view), shared with every list.
    view() {
      return listingView(this.token);
    },
    // A note's yield, or a fund's rate, is the headline; everything else leads with its price.
    yieldHero() {
      return this.view.kind === DealKinds.DEBT || (this.view.kind === DealKinds.FUND && Number(this.token.interest) > 0);
    },
    detail() {
      if (this.view.kind === DealKinds.DEBT) return { k: "Time left", v: this.timeRemaining };
      return this.view.pills[0] || null;
    },
    ctaLabel() {
      return [DealKinds.COMMODITY, DealKinds.LISTED].includes(this.view.kind) ? this.view.cta : "Buy Now";
    },
    change() {
      return ex.changePercentage(this.token);
    },
    timeRemaining() {
      const d = ex.daysRemaining(this.token);
      return d > 0 ? `${d} days` : "—";
    },
    // "Available to buy": what the active sell orders are worth, as the mobile app shows it.
    // Not the deal's remaining supply, which once a raise closes is the whole issue whether or
    // not anyone is selling.
    available() {
      return ex.money(ex.sellOrderValue(this.token), ex.tokenCurrency(this.token), 0);
    },
    minBuy() {
      return ex.money(ex.minPurchase(this.token), ex.tokenCurrency(this.token), 0);
    },
    badges() {
      return ex.listingBadges(this.token).slice(0, this.token.sharia_compliant ? 2 : 3);
    },
    detailRoute() {
      return {
        name: "TokenOverview",
        params: { symbol: this.token.symbol, id: this.token._id || this.token.id },
      };
    },
  },
  methods: {
    onImgError(e) {
      e.target.src = require("@/assets/img/token-icon.svg");
    },
  },
};
</script>

<style lang="scss" scoped>
.market-tile {
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  padding: 20px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: #4d84ff;
    box-shadow: 0 4px 20px rgba(77, 132, 255, 0.12);
    transform: translateY(-2px);

    .buy-link {
      color: #4d84ff;
    }
  }
}

.tile-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.logo {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: #f8fafc;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.identity {
  flex: 1;
  min-width: 0;
}

.name {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
}

.symbol {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.hero-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}


.hero-metric {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.price-line {
  display: flex;
  align-items: center;
}

.price-sub {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.metric-value {
  font-size: 20px;
  font-weight: 500;
  line-height: 1.1;
  color: #0f172a;
}

.metric-label {
  font-size: 11px;
  color: #64748b;
}

.tile-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  border-top: 1px solid #e2e8f0;
  padding-top: 14px;
}

.stat-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  flex: 1;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 10px;
  color: #64748b;
}

.stat-value {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}

.buy-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
  transition: color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }
}

@media (max-width: 640px) {
  .tile-footer {
    flex-direction: column;
    align-items: stretch;
  }
  .buy-link {
    justify-content: flex-end;
  }
}
</style>
